import { Box } from '@jfs/ui-new'
import { useFocusEffect, useNavigation } from '@react-navigation/native'
import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import { ListNew } from '../../components/ListNew'
import { useLazyApiData, useStores } from '../../data/store'
import { clean, isAccessible } from '../../utils'
import { ApprovalTypes, MDR_FEE_PACKAGE_FILETYPE, ROUTING_PACKAGE_FILETYPE } from '../../utils/constants'
import { ApprovalActionForm } from './ApprovalActionModalData'

interface MasterApprovalsProps {
  type: string
  title?: string
  showCurrentData?: boolean
  showUpdatedData?: boolean
  hideTitle?: boolean
}

const fileTypes = {
  [ApprovalTypes.FEE_PACKAGE]: MDR_FEE_PACKAGE_FILETYPE,
  [ApprovalTypes.FEE_PACKAGE_FILE_UPLOAD]: MDR_FEE_PACKAGE_FILETYPE,
  [ApprovalTypes.FEE_PACKAGE_MID]: MDR_FEE_PACKAGE_FILETYPE,
  [ApprovalTypes.PACKAGE]: ROUTING_PACKAGE_FILETYPE,
  [ApprovalTypes.FILE_ROUTING]: ROUTING_PACKAGE_FILETYPE,
  [ApprovalTypes.PACKAGE_MID]: ROUTING_PACKAGE_FILETYPE,
}

const MasterApprovals: FC<MasterApprovalsProps> = ({
  type,
  title = '',
  showCurrentData,
  showUpdatedData,
  hideTitle = false,
}: MasterApprovalsProps) => {
  const [selectedApprovalData, setApprovalData] = useState(null)
  const [approvalData, setApprovalType] = useState({})
  const [isExpanded, setisExpanded] = useState<{ isExpanded: boolean; value: number }>({ isExpanded: false, value: -1 })
  const navigation = useNavigation()

  const {
    masterApprovals,
    cardApprovals,
    netBanking,
    merchant,
    fileRoutingApprovals,
    netBankingList,
    entityList,
    cardList,
    merchantList,
    bankingCodesList,
    routingRuleList,
    accessConfig,
    packageApproval,
    package2I,
    package2MId,
    packageList,
    package2IList,
    package2MIdList,
    excelFile,
    excelFileList,
    bankingCodeApprovals,
    getFilteredEntityList,
    getFilteredCardList,
    getFilteredNetBankingList,
    getFilteredMerchantList,
    getFilteredBankingCodesList,
    getFilterdRoutingRuleList,
    getFilteredPackageList,
    getFilteredPackageInstrument,
    getFilteredPackageMid,
    getFilteredExcelFileList,
    getFilteredMerchantPayoutConfigList,
    getFilteredCrDrAdjustmentList,
    merchantPayoutConfigApprovals,
    crDrAdjustmentApprovals,
    merchantPayoutList,
    riskConfigTxnApprovals,
    riskConfigMidApprovals,
    payoutCmsApprovals,
    getFilteredPayoutCmsList,
    getFilteredRiskControlMidList,
    getFilteredRiskControlTxnList,
    exportFileAsync,
  } = useStores((store) => ({
    masterApprovals: store.approvals.getPendingMasterApprovals,
    cardApprovals: store.approvals.getPendingCardApprovals,
    netBanking: store.approvals.getPendingNetbankingApprovals,
    merchant: store.approvals.getPendingMerchantApprovals,
    fileRoutingApprovals: store.approvals.getFileRoutingPendingApprovals,
    packageApproval: store.approvals.getPendingPackageApprovals,
    package2I: store.approvals.getPendingPackageToInstrumentApprovals,
    package2MId: store.approvals.getPendingPackageToMIdApprovals,
    excelFile: store.approvals.getPendingGenerteFileApprovals,
    bankingCodeApprovals: store.approvals.getPendingBankingCodeApprovals,
    merchantPayoutConfigApprovals: store.approvals.getMerchantPayoutConfigApprovals,
    crDrAdjustmentApprovals: store.approvals.getCrDrAdjustmentApprovals,
    riskConfigTxnApprovals: store.approvals.getRiskControlTransactionApprovals,
    riskConfigInvoiceApprovals: store.approvals.getRiskControlInvoiceApprovals,
    riskConfigMidApprovals: store.approvals.getRiskControlMidApprovals,
    payoutCmsApprovals: store.approvals.getPayoutCmsApprovals,

    entityList: store.approvals.getEntityList(),
    cardList: store.approvals.card,
    netBankingList: store.approvals.netBanking,
    merchantList: store.approvals.merchant,
    bankingCodesList: store.approvals.bankingCode,
    routingRuleList: store.approvals.fileUpload,
    accessConfig: store.user.getAccessConfig,
    packageList: store.approvals.package,
    package2IList: store.approvals.packageInstrument,
    package2MIdList: store.approvals.packageMId,
    excelFileList: store.approvals.excelFile,

    getFilteredEntityList: store.approvals.getFilteredEntityList,
    getFilteredCardList: store.approvals.getFilteredCardList,
    getFilteredNetBankingList: store.approvals.getFilteredNetBankingList,
    getFilteredMerchantList: store.approvals.getFilteredMerchantList,
    getFilteredBankingCodesList: store.approvals.getFilteredBankingCodesList,
    getFilterdRoutingRuleList: store.approvals.getFilterdRoutingRuleList,
    getFilteredPackageList: store.approvals.getFilteredPackageList,
    getFilteredPackageInstrument: store.approvals.getFilteredPackageInstrument,
    getFilteredPackageMid: store.approvals.getFilteredPackageMid,
    getFilteredExcelFileList: store.approvals.getFilteredExcelFileList,
    getFilteredMerchantPayoutConfigList: store.approvals.getFilteredMerchantPayoutConfigList,
    getFilteredCrDrAdjustmentList: store.approvals.getFilteredCrDrAdjustmentList,
    merchantPayoutList: store.approvals.merchantPayout,
    getFilteredPayoutCmsList: store.approvals.getFilteredPayoutCmsList,
    getFilteredRiskControlMidList: store.approvals.getFilteredRiskControlMidList,
    getFilteredRiskControlTxnList: store.approvals.getFilteredRiskControlTxnList,
    exportFileAsync: store.exportFileAsync,
  }))

  const getFilteredApprovalData = useCallback(
    (allData: any, keysArray?: any[], searchString?: string) => {
      const getTypeData = {
        [ApprovalTypes.ENTITY]: getFilteredEntityList(allData, keysArray, searchString),
        [ApprovalTypes.CARDMAPPING]: getFilteredCardList(allData, keysArray, searchString),
        [ApprovalTypes.NETBANKING]: getFilteredNetBankingList(allData, keysArray, searchString),
        [ApprovalTypes.MERCHANT]: getFilteredMerchantList(allData, keysArray, searchString),
        [ApprovalTypes.FILE_ROUTING]: getFilterdRoutingRuleList(allData, keysArray, searchString),
        [ApprovalTypes.PACKAGE]: getFilteredPackageList(allData, keysArray, searchString),
        [ApprovalTypes.PACKAGE_INSTRUMENT]: getFilteredPackageInstrument(allData, keysArray, searchString),
        [ApprovalTypes.PACKAGE_MID]: getFilteredPackageMid(allData, keysArray, searchString),
        [ApprovalTypes.BANKING_CODE]: getFilteredBankingCodesList(allData, keysArray, searchString),
        [ApprovalTypes.GENERATE_EXCEL_FILE]: getFilteredExcelFileList(allData, keysArray, searchString),
        [ApprovalTypes.FEE_PACKAGE]: getFilteredPackageList(allData, keysArray, searchString),
        [ApprovalTypes.FEE_PACKAGE_FILE_UPLOAD]: getFilterdRoutingRuleList(allData, keysArray, searchString),
        [ApprovalTypes.FEE_PACKAGE_MID]: getFilteredPackageMid(allData, keysArray, searchString),
        [ApprovalTypes.MERCHANT_PAYOUT]: getFilteredMerchantPayoutConfigList(allData, keysArray, searchString),
        [ApprovalTypes.CREDIT_ADJUSTMENT]: getFilteredCrDrAdjustmentList(allData, keysArray, searchString),
        [ApprovalTypes.PAYOUT_CMS]: getFilteredPayoutCmsList(allData, keysArray, searchString),
        [ApprovalTypes.RISK_CONFIG_MID]: getFilteredRiskControlMidList(allData, keysArray, searchString),
        [ApprovalTypes.RISK_CONFIG_TXNS]: getFilteredRiskControlTxnList(allData, keysArray, searchString),
      }
      return getTypeData[type] ?? []
    },
    [
      getFilteredEntityList,
      getFilteredCardList,
      getFilteredNetBankingList,
      getFilteredMerchantList,
      getFilterdRoutingRuleList,
      getFilteredPackageList,
      getFilteredPackageInstrument,
      getFilteredPackageMid,
      getFilteredBankingCodesList,
      getFilteredExcelFileList,
      getFilteredMerchantPayoutConfigList,
      getFilteredCrDrAdjustmentList,
      getFilteredPayoutCmsList,
      getFilteredRiskControlMidList,
      getFilteredRiskControlTxnList,
      type,
    ],
  )

  const hasAccess = useCallback(
    (dataType: string) => {
      const getTypeData = {
        [ApprovalTypes.ENTITY]: ['entity_config', 'entity_master'],
        [ApprovalTypes.CARDMAPPING]: ['entity_config', 'card_type_mapping'],
        [ApprovalTypes.NETBANKING]: ['entity_config', 'net_bank_mapping'],
        [ApprovalTypes.MERCHANT]: ['merchant_config', 'merchant_mapping'],
        [ApprovalTypes.FILE_ROUTING]: ['package_file_upload', 'rules'],
        [ApprovalTypes.PACKAGE]: ['routing', 'package'],
        [ApprovalTypes.PACKAGE_INSTRUMENT]: ['routing', 'package_instrument_mapping'],
        [ApprovalTypes.PACKAGE_MID]: ['routing', 'mid_package_mapping'],
        [ApprovalTypes.BANKING_CODE]: ['sys_control_config', 'banking_codes'],
        [ApprovalTypes.GENERATE_EXCEL_FILE]: ['routing', 'rule_package_mapping'],
        [ApprovalTypes.FEE_PACKAGE]: ['mdr', 'package'],
        [ApprovalTypes.FEE_PACKAGE_FILE_UPLOAD]: ['mdr', 'package_file_upload'],
        [ApprovalTypes.FEE_PACKAGE_MID]: ['mdr', 'mid_package_mapping'],
        [ApprovalTypes.MERCHANT_PAYOUT]: ['payout_config'],
        [ApprovalTypes.CREDIT_ADJUSTMENT]: ['ess', 'crdr_adjustment'],
      }
      const access = accessConfig(getTypeData[dataType] ?? [])
      return isAccessible(access, 'approve')
    },
    [accessConfig],
  )

  const [getApprovalApiCaller, { state, data }] = useLazyApiData(masterApprovals)
  const [lazyExportFileAsync,exportFileAsyncHandler]= useLazyApiData(exportFileAsync)

  const searchFormRef = React.useRef(null)

  const abortControllerRef = useRef<AbortController>()
  const getAbortController = () => {
    abortControllerRef.current = new AbortController()
    return abortControllerRef.current
  }

  useFocusEffect(
    useCallback(() => {
      getApprovalApiCaller(
       type===ApprovalTypes.PAYOUT_CMS|| hasAccess(type),
        searchFormRef?.current?.getValues() ?? {},
        { page: 1, size: 10 },
        fileTypes[type],
        type,
      )

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigation]),
  )

  useEffect(() => {
    if (state === 'fulfilled') {
      // setListData(getFilteredApprovalData(data))
    }
  }, [state, getFilteredApprovalData, type])

  // const getData = () => {
  //   const list = getApprovalData(type)
  //   return list
  // }

  const onDismiss = () => {
    setisExpanded({ isExpanded: false, value: -1 })
  }

  const keyExtractor = (item: any): string => {
    return '' + item.id
  }

  const onSuccess = () => {
    setisExpanded({ isExpanded: false, value: -1 })
    getApprovalApiCaller(
      type===ApprovalTypes.PAYOUT_CMS|| hasAccess(type),
      searchFormRef?.current?.getValues(),
      { page: 1, size: 10 },
      fileTypes[type],
      type,
    )
  }

  const fetchDataFromServer = useCallback(
    (data, paginationInfo) => getApprovalApiCaller(type===ApprovalTypes.PAYOUT_CMS||hasAccess(type), data, paginationInfo, fileTypes[type], type),
    [getApprovalApiCaller, hasAccess, type],
  )

  const headTitles = [{ id: 'ID' }, { key: 'Key' }, { username: 'Username' }, { approvalType: 'Approval Type' }]

  const getExpandView = () => {
    return (
      <Box onPress={onDismiss} w={'100%'} underlayColor="TRANSPARENT">
        <Box alignSelf="flex-start" w={'100%'}>
          <ApprovalActionForm
            approvalData={approvalData}
            type={type}
            data={selectedApprovalData}
            onSuccess={onSuccess}
            showCurrentData={showCurrentData}
            showUpdatedData={showUpdatedData}
          />
        </Box>
      </Box>
    )
  }

  const handleOnPress = ({ item }: any) => {
    console.log('handleOnPress', item)
    const data = () => {
      if (type === 'FILE_ROUTING' || type === 'FILE_UPLOAD') {
        return item?.fileUploadData
      }
      if (type === 'BANKING_CODE') {
        return item?.bankingCodeData
      }
      if (type === 'MERCHANT_PAYOUT') {
        return item?.merchantPayoutData
      }
      if (type === ApprovalTypes.CREDIT_ADJUSTMENT) {
        return item?.crDrAdjustmentData
      }
      if (type === ApprovalTypes.PAYOUT_CMS) {
        return item?.settlement
      }
      return item?.data
    }
    const cleanedObject = clean(data())
    setApprovalType(item)
    setApprovalData(cleanedObject)
    setisExpanded({ isExpanded: !isExpanded.isExpanded, value: isExpanded.value === item?.id ? -1 : item?.id })
  }

  const exportFile = () => {
    lazyExportFileAsync({...(searchFormRef?.current?.getValues() ?? {}), fileType:fileTypes[type],}, type,   type===ApprovalTypes.PAYOUT_CMS|| hasAccess(type),getAbortController)
  }

  return (
    <Box>
      <ListNew
        searchData={SEARCH_FILTERS as any}
        updatedPaginationInfo={{
          ...(data?.pageNo !== undefined && { page: data?.pageNo }),
          ...(data?.totalElements !== undefined && {
            total: data?.totalElements,
          }),
        }}
        isSearchButtonLoading={state === 'pending'}
        apiHandler={{ state, data }}
        fetchDataFromServer={fetchDataFromServer}
        onRowItemClick={handleOnPress}
        keyExtractor={keyExtractor}
        headTitle={headTitles}
        searchFormRef={searchFormRef}
        sizeConfig={[2.5, 2.5, 2.5, 2.5]}
        data={getFilteredApprovalData(data?.content)}
        showExpandArrow={true}
        expandView={getExpandView}
        isExpanded={isExpanded.isExpanded}
        expandedIndex={isExpanded.value}
        exportFile={exportFile}
        isColumnSearchEnabled={true}
        hideTitle={hideTitle}
        listTitle={title}
        isExportButtonLoading={exportFileAsyncHandler.state==="pending"}
      />
    </Box>
  )
}

export { MasterApprovals }

const SEARCH_FILTERS = [
  [
    { key: 'id', type: 'string', placeHolder: 'ID' },
    { key: 'key', type: 'string', placeHolder: 'key' },
    { key: 'userName', type: 'string', placeHolder: 'Username' },
    {
      key: 'approvalType',
      type: 'dropdown',
      placeHolder: 'Approval Type',
      data: [
        { id: 1, value: 'NO_APPROVAL' },
        { id: 2, value: 'NEW_APPROVAL' },
        { id: 3, value: 'UPDATE_APPROVAL' },
        { id: 4, value: 'DELETE_APPROVAL' },
      ],
    },
  ],
]
